"use client";

import { useMemo, useState, type AnchorHTMLAttributes, type ReactNode } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  Controller,
  useForm,
  type FieldValues,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type {
  CCategoryAdvertisementArticle,
  CCategoryAdvertisementContactForm,
  CCategoryAdvertisementContent,
  CCategoryAdvertisementItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import {
  Checkbox,
  Field,
  FormStatus,
  Honeypot,
  Input,
  PhoneInput,
  Select,
  SubmitButton,
  Textarea,
} from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { toast } from "@/lib/notifications/toast";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import {
  cCategoryAdvertisementTranslations,
  type CCategoryAdvertisementTranslations,
} from "./CCategoryAdvertisement.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CCategoryAdvertisement.module.css";

/**
 * CCategoryAdvertisement - katalog ogloszen (`c_category_advertisement`).
 *
 * **Data source:** `C_CATEGORY_ADVERTISEMENT_CONTROLLER::getApiContent()` zwraca:
 *   - `view === 'list'` -> paginowana lista ogloszen w bieżącej kategorii +
 *     formularz wyszukiwarki (typy z parent'a c_advertisements) + button "Dodaj"
 *   - `view === 'search'` -> wyniki wyszukiwarki (po submit'cie formularza)
 *   - `view === 'article'` -> pojedyncze ogloszenie (po `?article_id=N`):
 *     pelna tresc + galeria zdjec + dane kontaktowe wystawcy
 *   - `view === 'article_not_found'` -> 404 sygnal (artykul nieopublikowany)
 *
 * **Security:** `text`/`short_text` to HTML z admin panelu (trusted) - OK dla
 * `dangerouslySetInnerHTML` (spojnie z CNews/CBlog/CAdvertisements).
 */
export function CCategoryAdvertisement({ content }: CCategoryAdvertisementProps) {
  const t = useT(cCategoryAdvertisementTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  if (content.view === "article_not_found") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.articleNotFound}</div>
      </div>
    );
  }

  if (content.view === "article" && content.article) {
    return (
      <div className={styles.root}>
        <ArticleView
          article={content.article}
          listHref={content.list_href ?? null}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <SearchForm content={content} t={t} />
      {content.add_href && (
        <div className={styles.topActions}>
          <SmartLink href={content.add_href} className={styles.addButton}>
            {t.addAdvert}
          </SmartLink>
        </div>
      )}
      <ListView content={content} t={t} />
    </div>
  );
}

export type CCategoryAdvertisementProps = {
  content: CCategoryAdvertisementContent;
};

/* Search form (RHF + Zod) - identyczny pattern jak CAdvertisements */

function buildSearchSchema(t: CCategoryAdvertisementTranslations) {
  const optionalPrice = z
    .string()
    .trim()
    .refine((v) => v === "" || /^\d+([.,]\d{1,2})?$/.test(v), {
      message: t.validation.invalidPrice,
    });

  return z.object({
    keyword: z.string().trim(),
    type_id: z.string(),
    price_from: optionalPrice,
    price_to: optionalPrice,
  });
}

type SearchFormValues = z.infer<ReturnType<typeof buildSearchSchema>>;

function SearchForm({
  content,
  t,
}: {
  content: CCategoryAdvertisementContent;
  t: CCategoryAdvertisementTranslations;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const schema = useMemo(() => buildSearchSchema(t), [t]);
  const initial = content.search;
  const types = content.types ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SearchFormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      keyword: initial?.keyword ?? "",
      type_id: initial?.type_id != null ? String(initial.type_id) : "",
      price_from: initial?.price_from != null ? String(initial.price_from) : "",
      price_to: initial?.price_to != null ? String(initial.price_to) : "",
    },
  });

  const onSubmit = (values: SearchFormValues) => {
    const qs = new URLSearchParams();
    qs.set("search_submitted", "1");
    if (values.keyword !== "") qs.set("keyword", values.keyword);
    if (values.type_id !== "") qs.set("type_id", values.type_id);
    if (values.price_from !== "")
      qs.set("price_from", values.price_from.replace(",", "."));
    if (values.price_to !== "")
      qs.set("price_to", values.price_to.replace(",", "."));
    router.push(`${pathname}?${qs.toString()}`);
  };

  return (
    <section className={styles.searchSection} aria-labelledby="ccat-search-title">
      <h2 id="ccat-search-title" className={styles.searchTitle}>
        {t.searchTitle}
      </h2>
      <form
        className={styles.form}
        role="search"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className={styles.fieldsGrid}>
          <Field label={t.fields.keyword} className={styles.gridSpan2}>
            <Input type="search" autoComplete="off" {...register("keyword")} />
          </Field>
          <Field label={t.fields.type}>
            <Select placeholder={t.allTypes} {...register("type_id")}>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.fields.priceFrom} error={errors.price_from?.message}>
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              {...register("price_from")}
            />
          </Field>
          <Field label={t.fields.priceTo} error={errors.price_to?.message}>
            <Input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              {...register("price_to")}
            />
          </Field>
        </div>
        <div className={styles.formActions}>
          <SubmitButton>{t.searchBtn}</SubmitButton>
        </div>
      </form>
    </section>
  );
}

/* List view */

function ListView({
  content,
  t,
}: {
  content: CCategoryAdvertisementContent;
  t: CCategoryAdvertisementTranslations;
}) {
  const items: CCategoryAdvertisementItem[] = Array.isArray(content.items) ? content.items : [];
  const isSearch = content.view === "search";
  const totalCount = content.pagination?.total_count ?? 0;

  if (items.length === 0) {
    return <div className={styles.empty}>{isSearch ? t.noAdsFound : t.noAdsInCategory}</div>;
  }

  return (
    <section className={styles.resultsSection} aria-labelledby="ccat-results-title">
      {isSearch && (
        <h2 id="ccat-results-title" className={styles.resultsTitle}>
          {t.resultsTitle(totalCount)}
        </h2>
      )}
      <ul className={styles.list}>
        {items.map((item) => (
          <ResultCard key={item.id} item={item} t={t} />
        ))}
      </ul>
    </section>
  );
}

function ResultCard({
  item,
  t,
}: {
  item: CCategoryAdvertisementItem;
  t: CCategoryAdvertisementTranslations;
}) {
  const teaserHtml = item.short_text || item.text || "";
  return (
    <li className={styles.card}>
      <SmartLink href={item.href} className={styles.cardLink} aria-label={item.name}>
        <div className={styles.thumbWrap}>
          {item.picture ? (
            <img
              src={item.picture.thumb_src}
              alt={item.picture.name}
              width={item.picture.width ?? undefined}
              height={item.picture.height ?? undefined}
              className={styles.thumb}
              loading="lazy"
            />
          ) : (
            <ImagePlaceholder />
          )}
        </div>
        <div className={styles.body}>
          <h3 className={styles.cardTitle}>{item.name}</h3>
          <div className={styles.meta}>
            {item.type_name && (
              <span className={styles.metaItem}>
                <span className={styles.metaLabel}>{t.fields.type}:</span> {item.type_name}
              </span>
            )}
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>{t.price}:</span>{" "}
              {item.negotiated_price
                ? t.negotiatedPrice
                : item.price != null
                  ? t.formatPrice(item.price)
                  : t.priceNotSet}
            </span>
          </div>
          {teaserHtml && (
            /* <div> nie <p> - tekst bywa HTML; spojnie z CNews/CBlog. */
            <div
              className={styles.teaser}
              dangerouslySetInnerHTML={{ __html: teaserHtml }}
            />
          )}
          <span className={styles.more}>{t.readMore}</span>
        </div>
      </SmartLink>
    </li>
  );
}

/* Article view */

function ArticleView({
  article,
  listHref,
  t,
}: {
  article: CCategoryAdvertisementArticle;
  listHref: string | null;
  t: CCategoryAdvertisementTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;

  /* Layout 't' / 'tl' / 'tr' = galeria nad tekstem; pozostale (bot/none) = pod. */
  const galleryOnTop =
    article.layout === "t" || article.layout === "tl" || article.layout === "tr";
  const galleryAlign =
    article.layout === "tl" ? "left" :
      article.layout === "tr" ? "right" : "center";

  return (
    <article className={styles.article}>
      {(backHref !== null || listHref) && (
        <div className={styles.articleBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.articleBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.articleBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.articleHeader}>
        <h2 className={styles.articleTitle}>{article.name}</h2>
        <div className={styles.articleMeta}>
          {article.type_name && (
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>{t.fields.type}:</span> {article.type_name}
            </span>
          )}
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>{t.price}:</span>{" "}
            {article.negotiated_price
              ? t.negotiatedPrice
              : article.price != null
                ? t.formatPrice(article.price)
                : t.priceNotSet}
          </span>
        </div>
      </header>

      {galleryOnTop && article.images.length > 0 && (
        <Gallery images={article.images} align={galleryAlign} />
      )}

      {article.text && (
        <div
          className={styles.articleText}
          dangerouslySetInnerHTML={{ __html: article.text }}
        />
      )}

      {!galleryOnTop && article.images.length > 0 && (
        <Gallery images={article.images} align="center" />
      )}

      <ContactBox
        owner={article.owner}
        contactForm={article.contact_form}
        articleId={article.id}
        t={t}
      />
    </article>
  );
}

function Gallery({
  images,
  align,
}: {
  images: CCategoryAdvertisementArticle["images"];
  align: "left" | "right" | "center";
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);

  /* Lightbox slajdy z pelnego zbioru zdjec - indeks z siatki = indeks slajdu.
     Wzorzec spojny z `CPhotoGallery` (ten sam pakiet `yet-another-react-lightbox`). */
  const slides = useMemo(
    () =>
      images.map((img) => ({
        src: img.full_src,
        width: img.width ?? undefined,
        height: img.height ?? undefined,
        alt: img.name,
      })),
    [images],
  );

  return (
    <>
      <div className={styles.gallery} data-align={align}>
        {images.map((img, i) => (
          <figure key={`${img.name}-${i}`} className={styles.galleryItem}>
            <button
              type="button"
              className={styles.galleryBtn}
              onClick={() => setOpenAt(i)}
              aria-label={img.name}
            >
              <img
                src={img.thumb_src}
                alt={img.name}
                width={img.width ?? undefined}
                height={img.height ?? undefined}
                loading={i === 0 ? "eager" : "lazy"}
                className={styles.galleryThumb}
              />
            </button>
          </figure>
        ))}
      </div>

      <Lightbox
        open={openAt !== null}
        index={openAt ?? 0}
        close={() => setOpenAt(null)}
        slides={slides}
      />
    </>
  );
}

function ContactBox({
  owner,
  contactForm,
  articleId,
  t,
}: {
  owner: CCategoryAdvertisementArticle["owner"];
  contactForm: CCategoryAdvertisementContactForm | null;
  articleId: number;
  t: CCategoryAdvertisementTranslations;
}) {
  const hasOwnerInfo = owner.city || owner.telephone || owner.login;
  if (!hasOwnerInfo && !contactForm) return null;

  return (
    <section className={styles.contactBox} aria-labelledby="ccat-contact-title">
      <h3 id="ccat-contact-title" className={styles.contactTitle}>
        {t.contact}
      </h3>
      {owner.city && (
        <p className={styles.contactRow}>
          <strong>{t.city}:</strong> {owner.city}
        </p>
      )}
      {owner.telephone && (
        <p className={styles.contactRow}>
          <strong>{t.telephone}:</strong>{" "}
          <a href={`tel:${owner.telephone.replace(/[^\d+]/g, "")}`}>{owner.telephone}</a>
        </p>
      )}
      {contactForm && (
        <ContactForm
          articleId={articleId}
          idCatalogFromEndpoint={extractIdCatalog(contactForm.endpoint)}
          antispam={contactForm.antispam}
          t={t}
        />
      )}
    </section>
  );
}

/**
 * Wyciaga `id_catalog` z endpointu wystawionego przez backend
 * (`/C_CATEGORY_ADVERTISEMENT_CONTROLLER/send_contact/{id_catalog}/{id_advert}`).
 * Frontend nie zna tego id z innego zrodla (article payload tez go nie ma),
 * a proxy potrzebuje go w body zeby wpiac w URL do backendu.
 */
function extractIdCatalog(endpoint: string): number {
  const parts = endpoint.split("/").filter(Boolean);
  const cand = Number(parts[parts.length - 2]);
  return Number.isFinite(cand) && cand > 0 ? cand : 0;
}

/* ─── Contact form (do wystawcy ogloszenia) ────────────────────────────── */

/**
 * Wartosci formularza. Zod v4 `.literal(true)` + narrowing w `.refine` daje
 * output typ `personal_data: true`, ktore koliduje z `defaultValues: false`.
 * Zeby uniknac kaskady castow uzywamy generyka `FieldValues` w `useForm`
 * (spojnie z CContactForm) - RHF nie egzekwuje typu output'u schema'y,
 * a Zod nadal robi walidacje runtime.
 */
type ContactFormValues = FieldValues & {
  name: string;
  email: string;
  telephone: string;
  text: string;
  personal_data: boolean;
};

function buildContactSchema(t: CCategoryAdvertisementTranslations) {
  return z.object({
    name: z.string().trim().min(1, t.contactForm.validation.requiredName),
    email: z
      .string()
      .trim()
      .min(1, t.contactForm.validation.requiredEmail)
      .email(t.contactForm.validation.invalidEmail),
    /* Telefon opcjonalny. PhoneInput zwraca "" gdy user nic nie wpisze;
       walidacja formatu leci server-side (libphonenumber przez IVALIDATOR). */
    telephone: z.string().trim(),
    text: z.string().trim().min(1, t.contactForm.validation.requiredText),
    /* RODO: musi byc zaznaczone. */
    personal_data: z.literal(true, {
      message: t.contactForm.validation.requiredPersonalData,
    }),
  });
}

function ContactForm({
  articleId,
  idCatalogFromEndpoint,
  antispam,
  t,
}: {
  articleId: number;
  idCatalogFromEndpoint: number;
  antispam: CCategoryAdvertisementContactForm["antispam"];
  t: CCategoryAdvertisementTranslations;
}) {
  const schema = useMemo(() => buildContactSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>({
    /* Cast: Zod v4 zwezalo `personal_data: z.literal(true)` output do `true`,
       co koliduje z generykiem `FieldValues` w RHF (i defaultValues.personal_data=false).
       Semantycznie resolver dziala poprawnie - walidacja Zod uruchamia sie
       w onSubmit; cast usuwa tylko konflikt typow structural. */
    resolver: zodResolver(schema) as unknown as Resolver<FieldValues>,
    mode: "onTouched",
    defaultValues: {
      name: "",
      email: "",
      telephone: "",
      text: "",
      personal_data: false,
    },
  });

  /* Helper: RHF przy `FieldValues` daje typ `FieldError | string`. Nasze pola
     to zwykle stringi (z Zod messages), wiec wyciagamy `message`. */
  const errorMsg = (name: keyof ContactFormValues): string | undefined => {
    const e = errors[name as string];
    if (!e) return undefined;
    if (typeof e === "string") return e;
    return typeof e.message === "string" ? e.message : undefined;
  };

  const [topError, setTopError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount - bypass Next.js SWR cache SSR'a. Scope
     to (idCatalog, idAdvert) - musi pasowac do `IANTISPAM::verifyToken` w
     backend'owym `IAPI_sendContact`. */
  const freshAntispam = useAntiSpamToken([idCatalogFromEndpoint, articleId]);

  const onSubmit = async (raw: FieldValues) => {
    /* Cast: walidacja Zod uruchomiona przez resolver gwarantuje ze `raw`
       ma ksztalt `ContactFormValues` - RHF typuje jako FieldValues
       (dynamiczny). */
    const values = raw as ContactFormValues;
    setTopError(null);

    const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
    if (ageSec < antispam.min_delay_seconds) {
      setTopError(
        t.contactForm.status.waitBeforeSubmit(antispam.min_delay_seconds - ageSec),
      );
      return;
    }

    if (!freshAntispam.token || !freshAntispam.timestamp) {
      setTopError(freshAntispam.error ?? t.contactForm.status.sendFailed);
      return;
    }

    const body: Record<string, unknown> = {
      id_catalog: idCatalogFromEndpoint,
      id_advert: articleId,
      token: freshAntispam.token,
      timestamp: freshAntispam.timestamp,
      [antispam.honeypot_field]: honeypot,
      name: values.name,
      email: values.email,
      telephone: values.telephone,
      text: values.text,
      personal_data: values.personal_data === true,
    };

    try {
      const res = await fetch(`/api/c-category-advertisement/send-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        error?: string;
        code?: number;
        errors?: Record<string, string>;
      };

      if (!res.ok || !json.ok) {
        if (
          json.errors &&
          typeof json.errors === "object" &&
          Object.keys(json.errors).length > 0
        ) {
          for (const [name, msg] of Object.entries(json.errors)) {
            if (typeof msg !== "string") continue;
            /* Nazwy pol per backend `IAPI_sendContact` - `name`, `email`,
               `telephone`, `text`, `personal_data`. Cast wymagany bo RHF
               ma typowany union po kluczach ContactFormValues. */
            if (
              name === "name" ||
              name === "email" ||
              name === "telephone" ||
              name === "text" ||
              name === "personal_data"
            ) {
              setError(name, { type: "server", message: msg });
            }
          }
        } else {
          setTopError(
            json.message ||
              (res.status === 429
                ? t.contactForm.status.rateLimit
                : t.contactForm.status.sendFailed),
          );
        }
        return;
      }

      toast.success(json.message || t.contactForm.status.sendSuccess);
      reset();
      setHoneypot("");
    } catch (err) {
      setTopError(
        err instanceof Error
          ? t.contactForm.status.networkError(err.message)
          : t.contactForm.status.networkErrorGeneric,
      );
    }
  };

  return (
    <div className={styles.contactFormWrap}>
      <h4 className={styles.contactFormTitle}>{t.contactForm.title}</h4>
      <form
        className={styles.contactForm}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className={styles.contactFieldsGrid}>
          <Field
            label={t.contactForm.fields.name}
            required
            error={errorMsg("name")}
          >
            <Input
              type="text"
              autoComplete="name"
              disabled={isSubmitting}
              prefix={<Icon name="user" />}
              {...register("name")}
            />
          </Field>
          <Field
            label={t.contactForm.fields.email}
            required
            error={errorMsg("email")}
          >
            <Input
              type="email"
              autoComplete="email"
              disabled={isSubmitting}
              prefix={<Icon name="envelope" />}
              {...register("email")}
            />
          </Field>
          <Field
            label={t.contactForm.fields.telephone}
            error={errorMsg("telephone")}
            className={styles.gridSpan2}
          >
            <Controller
              name="telephone"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value ?? ""}
                  onChange={(v) => field.onChange(v ?? "")}
                  disabled={isSubmitting}
                />
              )}
            />
          </Field>
        </div>

        <Field
          label={t.contactForm.fields.text}
          required
          error={errorMsg("text")}
        >
          <Textarea rows={5} disabled={isSubmitting} {...register("text")} />
        </Field>

        <div className={styles.contactCheckboxRow}>
          <Controller
            name="personal_data"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value === true}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
                disabled={isSubmitting}
                aria-invalid={!!errorMsg("personal_data") || undefined}
              >
                {t.contactForm.fields.personalData}
              </Checkbox>
            )}
          />
          {errorMsg("personal_data") && (
            <div className={styles.contactFieldError}>
              {errorMsg("personal_data")}
            </div>
          )}
        </div>

        <Honeypot
          name={antispam.honeypot_field}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />

        {topError && <FormStatus variant="error">{topError}</FormStatus>}

        <div className={styles.contactActions}>
          <SubmitButton
            loading={isSubmitting || !freshAntispam.ready}
            loadingLabel={t.contactForm.sending}
            disabled={freshAntispam.error != null}
          >
            {t.contactForm.submit}
          </SubmitButton>
        </div>
      </form>
    </div>
  );
}

/* Helpers */

function SmartLink({
  href,
  className,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  children: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const internal = extractInternalHref(href);
  if (internal !== null) {
    return (
      <Link href={internal} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}
